<template>
  <v-menu
    v-model="menuOpen"
    location="bottom start"
    :close-on-content-click="false"
    transition="scale-transition"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="outlined"
        rounded="lg"
        class="filter-style-btn"
      >
        <v-icon :icon="icon" start />
        {{ label }}
      </v-btn>
    </template>
    <v-card min-width="240" max-height="320" class="dropdown-card">
      <div class="dropdown-header">
        <button
          type="button"
          :class="['select-all-btn', allSelected ? 'checked' : '']"
          @click="$emit('select-all', !allSelected)"
        >
          {{ allSelected ? 'Unselect all' : 'Select all' }}
        </button>
      </div>
      <v-list class="py-0 dropdown-list">
        <v-list-item
          v-for="item in items"
          :key="item.id"
          class="dropdown-list-item"
          @click="$emit('toggle', item.id, !isSelected(item.id))"
        >
          <template #prepend>
            <div
              class="dropdown-checkbox-wrap"
              @click.stop
            >
              <v-checkbox-btn
                :model-value="isSelected(item.id)"
                color="brandPrimary"
                class="dropdown-checkbox"
                hide-details
                @update:model-value="$emit('toggle', item.id, $event)"
              />
            </div>
          </template>
          <span class="item-label text-body-2">{{ item.label }}</span>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
});

defineEmits(['toggle', 'select-all']);

const menuOpen = ref(false);

const selectedSet = computed(() => new Set(props.selectedIds));

const isSelected = (id) => selectedSet.value.has(id);

const allSelected = computed(() => {
  if (!props.items?.length) return false;

  return props.items.every((item) => isSelected(item.id));
});
</script>

<style scoped lang="less">
.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}

.dropdown-card {
  border: 0 !important;
  box-shadow: 0 10px 50px 0 fade(@grey-900, 10%) !important;
  border-radius: 8px;
}

.dropdown-header {
  padding: 8px 12px;
  border-radius: 8px 8px 0 0;
}

.select-all-btn {
  font-size: 13px;
  font-weight: 600;
  color: @text-darkest;
  cursor: pointer;
}

.select-all-btn:hover {
  color: @text-brand;
}

.select-all-btn.checked {
  color: @text-brand;
}

.dropdown-list {
  max-height: 280px;
  overflow-y: auto;
  border-radius: 0 0 8px 8px;
}

.dropdown-list-item {
  min-height: 36px;
  height: 36px;
  padding: 0 12px;
  cursor: pointer;
}

.item-label {
  color: fade(@grey-700, 95%);
}

.dropdown-list-item:hover .item-label {
  color: @text-brand;
}

.dropdown-checkbox-wrap {
  display: flex;
  align-items: center;
}

.dropdown-checkbox :deep(.v-selection-control) {
  min-height: 36px;
}

.dropdown-checkbox :deep(.v-selection-control__wrapper) {
  transform: scale(0.8);
  transform-origin: left center;
}
</style>
